import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { Session } from '@/types'
import ShareAccessSection from './components/ShareAccessSection'
import RequestAccessSection from './components/RequestAccessSection'
import MessagesSection from './components/MessagesSection'
import { useBiblionautasBadges } from '../../hooks/useBiblionautasBadges'
import styles from './Biblionautas.module.css'

type TabName = 'messages' | 'request' | 'share'

interface BiblionautasProps {
    session: Session
    onBack: () => void
    onViewLibrary: (userId: string, userName: string) => void
}

export function Biblionautas({ session, onBack, onViewLibrary }: Readonly<BiblionautasProps>) {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState<TabName>('messages')
    const prevActiveTabRef = useRef<TabName>(activeTab)

    const { counts, fetchCounts, updateLastSeen } = useBiblionautasBadges(session?.user?.id, {
        polling: false,
        autoFetch: true,
    })

    useEffect(() => {
        const handleTabChange = async () => {
            const prevTab = prevActiveTabRef.current
            if (prevTab === activeTab) return

            if (prevTab === 'share') {
                await updateLastSeen('last_seen_shares_at')
            }
            if (prevTab === 'request') {
                await updateLastSeen('last_seen_requests_at')
            }
            if (prevTab === 'messages') {
                await updateLastSeen('last_seen_messages_at')
            }

            await fetchCounts()

            prevActiveTabRef.current = activeTab
        }
        handleTabChange()
    }, [activeTab, updateLastSeen, fetchCounts])

    const handleBack = async () => {
        if (activeTab === 'share') await updateLastSeen('last_seen_shares_at')
        if (activeTab === 'request') await updateLastSeen('last_seen_requests_at')
        if (activeTab === 'messages') await updateLastSeen('last_seen_messages_at')
        onBack()
    }

    return (
        <div className={`${styles.biblionautasContainer} animate-fade-in-up`}>
            <div className={styles.biblionautasHeader}>
                <button onClick={handleBack} className={styles.backBtn} data-testid="biblionautas-back-btn">
                    ← {t('biblionautas.back')}
                </button>
                <h2 data-testid="biblionautas-title">👩‍🚀 {t('biblionautas.title')}</h2>
                <p className={styles.subtitle}>{t('biblionautas.subtitle')}</p>
            </div>

            <div className={styles.biblionautasTabs}>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'messages' ? styles.tabBtnActive : ''}`}
                    onClick={() => setActiveTab('messages')}
                    data-testid="tab-messages"
                >
                    💬 {t('biblionautas.tabs.messages')}
                    {counts.messages > 0 && <span className={styles.notificationBadge}>{counts.messages}</span>}
                </button>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'request' ? styles.tabBtnActive : ''}`}
                    onClick={() => setActiveTab('request')}
                    data-testid="tab-discover"
                >
                    📥 {t('biblionautas.tabs.discover')}
                    {counts.request > 0 && <span className={styles.notificationBadge}>{counts.request}</span>}
                </button>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'share' ? styles.tabBtnActive : ''}`}
                    onClick={() => setActiveTab('share')}
                    data-testid="tab-share"
                >
                    📤 {t('biblionautas.tabs.share')}
                    {counts.share > 0 && <span className={styles.notificationBadge}>{counts.share}</span>}
                </button>
            </div>

            <div className={styles.biblionautasContent}>
                {activeTab === 'share' && <ShareAccessSection session={session} />}
                {activeTab === 'request' && <RequestAccessSection session={session} onViewLibrary={onViewLibrary} />}
                {activeTab === 'messages' && <MessagesSection session={session} />}
            </div>
        </div>
    )
}
